import { PageEvents, WebPageEvents, WebPaths } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useStartDesktopSignIn } from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useActiveTheme } from 'shared/lib/mui';
import { ThemeModes } from 'shared/types/theme-modes';

import * as Styled from './desktop-sign-in.style';

export type DesktopSignInProps = {
  pageEvent: PageEvents | WebPageEvents;
};

export const DesktopSignIn: React.FC<DesktopSignInProps> = ({ pageEvent }) => {
  const { t } = useTranslation();
  const { start, browserUrl } = useStartDesktopSignIn();
  const [themeMode] = useActiveTheme();
  useTrackPageVisitEvent(pageEvent, undefined);

  const graphicPath = `/images/sign-in-on-web/sign-in-on-web-graphic-${
    themeMode === ThemeModes.Dark ? 'DM' : 'LM'
  }`;

  const handleSignIn = useCallback(() => {
    start(WebPaths.Login);
  }, [start]);

  const handleSignUp = useCallback(() => {
    start(WebPaths.Signup);
  }, [start]);

  const handleCopyLink = useCallback(() => {
    if (!browserUrl) {
      return;
    }
    // clipboard access can be denied by permission; copying is a convenience, not the only path
    navigator.clipboard?.writeText(browserUrl).catch(() => {});
  }, [browserUrl]);

  return (
    <Styled.Container>
      <Styled.LeftColumn>
        <Styled.Header>
          <Styled.Logo src="/griply-logo/griply-logo-text.svg" alt="Griply" />

          <Styled.Titles>
            <Styled.Title variant="h1">
              {t('forms.desktop-sign-in.title')}
            </Styled.Title>

            <Typography variant="body1">
              {t('forms.desktop-sign-in.description')}
            </Typography>
          </Styled.Titles>
        </Styled.Header>

        <Styled.Options>
          {browserUrl ? (
            <Typography variant="body2" component="span">
              {t('forms.desktop-sign-in.copy-link.question.label')}{' '}
              <Styled.LinkButton type="button" onClick={handleCopyLink}>
                <Typography variant="subtitle2" component="span">
                  {t('forms.desktop-sign-in.copy-link.label')}
                </Typography>
              </Styled.LinkButton>
            </Typography>
          ) : (
            <Button
              variant={Variants.Contained}
              color={Colors.Primary}
              size={Sizes.Large}
              onClick={handleSignIn}
            >
              {t('forms.desktop-sign-in.buttons.continue.label')}
            </Button>
          )}

          <Typography variant="body2" component="span" textAlign="center">
            {t('forms.login.no-account.question.label')}{' '}
            <Styled.LinkButton type="button" onClick={handleSignUp}>
              <Typography variant="subtitle2" component="span">
                {t('forms.desktop-sign-in.no-account.link.label')}
              </Typography>
            </Styled.LinkButton>
          </Typography>
        </Styled.Options>
      </Styled.LeftColumn>

      <Styled.RightColumn>
        <Styled.Graphic
          src={`${graphicPath}.png`}
          srcSet={`${graphicPath}@2x.png 2x, ${graphicPath}@3x.png 3x`}
          alt=""
        />
      </Styled.RightColumn>
    </Styled.Container>
  );
};
